/**
 * DviCustomerView - Public page for customers to view and respond to DVI findings
 * 
 * Route: /dvi/view/:token
 * No auth required - token-based access
 * 
 * Phase 3 Updates:
 * - Displays Finding Groups (not individual findings)
 * - Shows cover thumbnails per group
 * - Shows labor time estimates
 * - Shows "What We'll Do" procedure outline
 * - Groups findings by condition (Red → Yellow → Green)
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  ThumbsUp, 
  ThumbsDown, 
  Send, 
  Car,
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Finding {
  item_token: string;
  condition: 'green' | 'yellow' | 'red' | null;
  title: string | null;
  summary: string | null;
  what_well_do: string | null;
  estimated_labor_minutes: number;
  thumbnail_url: string | null;
  current_decision: 'none' | 'approved' | 'declined';
  customer_notes: string | null;
}

interface DviViewData {
  shop: {
    name: string;
    logo_url: string | null;
  };
  vehicle: {
    year?: number;
    make?: string;
    model?: string;
  } | null;
  findings: Finding[];
  session_status: string;
  already_responded: boolean;
}

type PageState = 'loading' | 'error' | 'active' | 'already_responded' | 'submitted';

const CONDITION_CONFIG = {
  green: { label: 'Good Condition', icon: CheckCircle, className: 'bg-green-500/10 text-green-600 border-green-500/20', bgColor: 'bg-green-50' },
  yellow: { label: 'Monitor Soon', icon: AlertTriangle, className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20', bgColor: 'bg-yellow-50' },
  red: { label: 'Needs Attention', icon: XCircle, className: 'bg-red-500/10 text-red-600 border-red-500/20', bgColor: 'bg-red-50' },
};

// Sort priority: red=0, yellow=1, green=2
const CONDITION_PRIORITY: Record<string, number> = { red: 0, yellow: 1, green: 2 };

export default function DviCustomerView() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [data, setData] = useState<DviViewData | null>(null);
  const [decisions, setDecisions] = useState<Record<string, { decision: 'approved' | 'declined' | null; notes: string }>>({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch session data on mount
  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage('Invalid link');
      return;
    }

    fetchSessionData();
  }, [token]);

  const fetchSessionData = async () => {
    try {
      const { data: response, error } = await supabase.functions.invoke('dvi-customer-view', {
        body: { token },
      });

      if (error) throw error;

      if (response.error) {
        setPageState('error');
        setErrorMessage(response.error);
        return;
      }

      setData(response);

      if (response.already_responded) {
        setPageState('already_responded');
      } else {
        setPageState('active');
        // Initialize decisions from existing data
        const initialDecisions: Record<string, { decision: 'approved' | 'declined' | null; notes: string }> = {};
        for (const finding of response.findings) {
          initialDecisions[finding.item_token] = {
            decision: finding.current_decision === 'none' ? null : finding.current_decision,
            notes: finding.customer_notes || '',
          };
        }
        setDecisions(initialDecisions);
      }
    } catch (error: any) {
      console.error('Error fetching DVI session:', error);
      setPageState('error');
      setErrorMessage(error.message || 'Failed to load inspection');
    }
  };

  const handleDecision = (itemToken: string, decision: 'approved' | 'declined') => {
    setDecisions(prev => ({
      ...prev,
      [itemToken]: {
        ...prev[itemToken],
        decision: prev[itemToken]?.decision === decision ? null : decision, // Toggle
      },
    }));
  };

  const handleNotesChange = (itemToken: string, notes: string) => {
    setDecisions(prev => ({
      ...prev,
      [itemToken]: {
        ...prev[itemToken],
        notes,
      },
    }));
  };

  const toggleExpanded = (itemToken: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemToken)) {
        next.delete(itemToken);
      } else {
        next.add(itemToken);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const submittableDecisions = Object.entries(decisions)
      .filter(([_, d]) => d.decision !== null)
      .map(([item_token, d]) => ({
        item_token,
        decision: d.decision as 'approved' | 'declined',
        notes: d.notes || undefined,
      }));

    if (submittableDecisions.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: response, error } = await supabase.functions.invoke('dvi-customer-respond', {
        body: { token, decisions: submittableDecisions },
      });

      if (error) throw error;

      if (response.success) {
        setPageState('submitted');
      }
    } catch (error: any) {
      console.error('Error submitting decisions:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const decisionCount = Object.values(decisions).filter(d => d.decision !== null).length;
  
  // Sort findings: Red first, then Yellow, then Green
  const sortedFindings = [...(data?.findings || [])].sort((a, b) => {
    const priorityA = a.condition ? CONDITION_PRIORITY[a.condition] ?? 3 : 3;
    const priorityB = b.condition ? CONDITION_PRIORITY[b.condition] ?? 3 : 3;
    return priorityA - priorityB;
  });
  
  const nonGreenFindings = sortedFindings.filter(f => f.condition !== 'green');
  const greenFindings = sortedFindings.filter(f => f.condition === 'green');

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading your inspection...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h1 className="text-xl font-semibold">Unable to Load Inspection</h1>
            <p className="text-muted-foreground">{errorMessage || 'This link may be invalid or expired.'}</p>
            <p className="text-sm text-muted-foreground">Please contact your service advisor for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already responded state
  if (pageState === 'already_responded') {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-lg mx-auto space-y-6 pt-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{data?.shop.name}</h1>
            {data?.vehicle && (
              <p className="text-muted-foreground">
                {data.vehicle.year} {data.vehicle.make} {data.vehicle.model}
              </p>
            )}
          </div>

          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
              <h2 className="text-xl font-semibold">Thank You!</h2>
              <p className="text-muted-foreground">
                Your inspection responses have been recorded. Your service advisor will review your decisions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Submitted state
  if (pageState === 'submitted') {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-lg mx-auto space-y-6 pt-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{data?.shop.name}</h1>
          </div>

          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
              <h2 className="text-xl font-semibold">Decisions Submitted!</h2>
              <p className="text-muted-foreground">
                Thank you for your response. Your service advisor has been notified and will proceed with the approved services.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Active state - show findings
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {data?.shop.logo_url ? (
              <img src={data.shop.logo_url} alt={data.shop.name} className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Car className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-semibold">{data?.shop.name}</h1>
              {data?.vehicle && (
                <p className="text-sm text-muted-foreground">
                  {data.vehicle.year} {data.vehicle.make} {data.vehicle.model}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold">Vehicle Inspection Results</h2>
          <p className="text-sm text-muted-foreground">
            Review the findings below and let us know which services you'd like to approve.
          </p>
        </div>

        {/* Non-green Findings (actionable) */}
        {nonGreenFindings.map((finding) => {
          const condition = finding.condition;
          const config = condition ? CONDITION_CONFIG[condition] : null;
          const currentDecision = decisions[finding.item_token]?.decision;
          const notes = decisions[finding.item_token]?.notes || '';
          const isExpanded = expandedIds.has(finding.item_token);

          return (
            <Card 
              key={finding.item_token} 
              className={cn(
                'overflow-hidden transition-colors',
                config?.bgColor
              )}
            >
              <CardContent className="p-4 space-y-3">
                {/* Header with thumbnail */}
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  {finding.thumbnail_url && (
                    <div className="w-20 h-20 rounded overflow-hidden shrink-0 bg-muted">
                      <img 
                        src={finding.thumbnail_url} 
                        alt={finding.title || 'Inspection'} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0 space-y-1">
                    {/* Condition Badge */}
                    {config && (
                      <Badge variant="outline" className={cn('gap-1', config.className)}>
                        <config.icon className="h-3 w-3" />
                        {config.label}
                      </Badge>
                    )}

                    {/* Title */}
                    <h3 className="font-medium">{finding.title || 'Inspection Finding'}</h3>

                    {/* Labor estimate */}
                    {finding.estimated_labor_minutes > 0 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Est. {finding.estimated_labor_minutes} min labor
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {finding.summary && (
                  <p className="text-sm text-muted-foreground">{finding.summary}</p>
                )}

                {/* Expandable: What We'll Do */}
                {finding.what_well_do && (
                  <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(finding.item_token)}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1 -ml-2">
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        What We'll Do
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="bg-muted/50 rounded p-3">
                        <p className="text-sm whitespace-pre-line">{finding.what_well_do}</p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Decision Buttons */}
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex gap-2">
                    <Button
                      variant={currentDecision === 'approved' ? 'default' : 'outline'}
                      size="sm"
                      className={cn(
                        'flex-1 gap-2',
                        currentDecision === 'approved' && 'bg-green-600 hover:bg-green-700'
                      )}
                      onClick={() => handleDecision(finding.item_token, 'approved')}
                    >
                      <ThumbsUp className="h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      variant={currentDecision === 'declined' ? 'default' : 'outline'}
                      size="sm"
                      className={cn(
                        'flex-1 gap-2',
                        currentDecision === 'declined' && 'bg-red-600 hover:bg-red-700'
                      )}
                      onClick={() => handleDecision(finding.item_token, 'declined')}
                    >
                      <ThumbsDown className="h-4 w-4" />
                      Decline
                    </Button>
                  </div>

                  {/* Optional Notes */}
                  {currentDecision && (
                    <Textarea
                      placeholder="Add a note (optional)..."
                      value={notes}
                      onChange={(e) => handleNotesChange(finding.item_token, e.target.value)}
                      className="text-sm min-h-[60px]"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Green findings summary */}
        {greenFindings.length > 0 && (
          <Card className="bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-700">
                  {greenFindings.length} item{greenFindings.length !== 1 ? 's' : ''} passed inspection
                </span>
              </div>
              {greenFindings.length <= 5 && (
                <ul className="mt-2 space-y-1 text-sm text-green-700">
                  {greenFindings.map(f => (
                    <li key={f.item_token}>• {f.title || 'Inspection item'}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky Submit Button */}
      {nonGreenFindings.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-4">
          <div className="max-w-lg mx-auto">
            <Button
              className="w-full gap-2"
              size="lg"
              onClick={handleSubmit}
              disabled={decisionCount === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Submit {decisionCount > 0 ? `${decisionCount} Decision${decisionCount > 1 ? 's' : ''}` : 'Decisions'}
            </Button>
            {decisionCount === 0 && (
              <p className="text-xs text-center text-muted-foreground mt-2">
                Select Approve or Decline on at least one item
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
